/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';

import { messages } from './messages';

const REINTRO_VIDEO_SRC = 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OAWR.mp4';

/**
 * Must match `HERO_BACKGROUND_COLOR` in the modal: the gradient fades the video's
 * left edge into the hero background. Kept as a local copy because
 * `@atlaskit/ui-styling-standard` forbids importing values used inside `cssMap`.
 */
const HERO_BACKGROUND_COLOR = '#F8F8F8';

const styles = cssMap({
	container: {
		position: 'absolute',
		right: 0,
		bottom: 0,
		zIndex: 1,
		transform: 'translate(89px, 4px)',
		width: 'min(541px, 90%)',
		aspectRatio: '105 / 59',
	},
	video: {
		display: 'block',
		width: '100%',
		height: '100%',
		objectFit: 'contain',
	},
	blend: {
		position: 'absolute',
		insetBlockStart: 0,
		insetBlockEnd: 0,
		insetInlineStart: 0,
		width: '90px',
		pointerEvents: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		backgroundImage: `linear-gradient(90deg, ${HERO_BACKGROUND_COLOR} 0%, transparent 100%)`,
	},
});

/**
 * Decorative, looping video of the Rovo prompt box shown in the hero of the
 * modal. Autoplays muted so it can start without user interaction. Positioned
 * absolutely, so it must be rendered inside a `position: relative` container.
 *
 * The video src is intentionally set after mount (via useEffect) to prevent the
 * browser from fetching the MP4 during the host page's initial load. Without
 * this deferral, autoPlay causes an eager network request that competes with
 * page assets and inflates VC90 p90 (Confluence Live Doc) in Statsig.
 */
export function PromptBoxVideo(): JSX.Element {
	const { formatMessage } = useIntl();
	const videoRef = useRef<HTMLVideoElement>(null);

	useEffect(() => {
		if (videoRef.current) {
			videoRef.current.src = REINTRO_VIDEO_SRC;
		}
	}, []);

	return (
		<div css={styles.container}>
			{/* eslint-disable-next-line jsx-a11y/media-has-caption -- decorative, silent looping clip */}
			<video
				ref={videoRef}
				css={styles.video}
				autoPlay
				loop
				muted
				playsInline
				aria-label={formatMessage(messages.chatPreviewPrompt)}
			></video>
			<div css={styles.blend} aria-hidden="true" />
		</div>
	);
}
